// src/pages/TelemedicinePage.jsx
import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../axiosInstance';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Container, Typography, Button, Card, CardContent, Grid, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, List, ListItem, ListItemText, ListItemIcon, Box,
  CircularProgress, Alert, Snackbar, useTheme, Avatar, Divider, Chip, alpha, Paper,
  useMediaQuery, LinearProgress, IconButton, Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import EmailIcon from '@mui/icons-material/Email';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SearchIcon from '@mui/icons-material/Search';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LaptopIcon from '@mui/icons-material/Laptop';
import VideocamIcon from '@mui/icons-material/Videocam';
import ConnectWithoutContactIcon from '@mui/icons-material/ConnectWithoutContact';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { motion } from 'framer-motion';
import VideoCallComponent from '../components/VideoCallComponent';

// Helper para detectar cancelaciones (StrictMode / AbortController)
const isCancel = (e) =>
  e?.name === 'CanceledError' ||
  e?.name === 'AbortError' ||
  e?.code === 'ERR_CANCELED' ||
  e?.message === 'canceled';

// Styled components
const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  backgroundColor: theme.palette.mode === 'dark'
    ? alpha(theme.palette.background.paper, 0.6)
    : theme.palette.grey[100],
  transition: 'transform 0.3s, box-shadow 0.3s',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const HeaderIcon = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  width: 56,
  height: 56,
  marginRight: theme.spacing(2),
  boxShadow: theme.shadows[3],
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(4),
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 20,
  padding: '12px 24px',
  fontWeight: 500,
  boxShadow: theme.shadows[2],
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
}));

const SearchField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
  },
  marginBottom: theme.spacing(2),
}));

const TelemedicinePage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const openFetchCtrlRef = useRef(null); // AbortController para recarga al abrir el diálogo

  const [existingPatientDialogOpen, setExistingPatientDialogOpen] = useState(false);
  const [newPatientDialogOpen, setNewPatientDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [patients, setPatients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [newPatientEmail, setNewPatientEmail] = useState('');
  const [newPatientName, setNewPatientName] = useState('');
  const [inCall, setInCall] = useState(false);
  const [roomId, setRoomId] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [activeSessions, setActiveSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);

  // Interceptores de depuración (solo dev)
  useEffect(() => {
    if (!import.meta.env.DEV) return;

    const reqId = axiosInstance.interceptors.request.use((request) => {
      console.log('Request:', request.method, request.url, request.data);
      return request;
    });
    const resId = axiosInstance.interceptors.response.use(
      (response) => {
        console.log('Response:', response.status, response.data);
        return response;
      },
      (error) => {
        if (isCancel(error)) {
          return Promise.reject(error);
        }
        console.log('Response Error:', error.response?.status, error.response?.data);
        return Promise.reject(error);
      }
    );

    return () => {
      axiosInstance.interceptors.request.eject(reqId);
      axiosInstance.interceptors.response.eject(resId);
    };
  }, []);

  // Carga inicial cuando auth está listo
  useEffect(() => {
    if (loading || !user) return;

    const ctrl = new AbortController();
    (async () => {
      await Promise.allSettled([
        fetchPatients(ctrl.signal),
        fetchActiveSessions(ctrl.signal),
      ]);
    })();

    return () => ctrl.abort();
  }, [loading, user]);

  const fetchPatients = async (signal) => {
    if (!user) return;
    setIsLoading(true);
    try {
      const response = await axiosInstance.get('/pacientes/', { signal });
      setPatients(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      if (isCancel(error)) return;
      console.error('Error fetching patients:', error);
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchActiveSessions = async (signal) => {
    setLoadingSessions(true);
    try {
      const response = await axiosInstance.get('/telemedicine/active-sessions/', { signal });
      setActiveSessions(response.data || []);
    } catch (error) {
      if (isCancel(error)) return;
      console.error('Error fetching active sessions:', error);
      handleApiError(error);
    } finally {
      setLoadingSessions(false);
    }
  };

  const rejoinSession = (roomId, sessionId, patientName) => {
    setRoomId(roomId);
    setSelectedPatient({
      nombres: patientName?.split(' ')[0] || 'Paciente',
      apellidos: patientName?.split(' ').slice(1).join(' ') || '',
    });
    setInCall(true);
  };

  const handleApiError = (error) => {
    if (error?.response?.status === 401) {
      showSnackbar('Sesión expirada. Por favor inicie sesión nuevamente.', 'error');
    } else if (error?.response?.status === 404) {
      showSnackbar('Recurso no encontrado. Verifica la URL del endpoint.', 'error');
    } else {
      showSnackbar(`Error: ${error.message || 'Ocurrió un problema'}`, 'error');
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleExistingPatientDialogOpen = () => {
    if (!user) {
      showSnackbar('Por favor inicie sesión para continuar.', 'warning');
      return;
    }
    setExistingPatientDialogOpen(true);
    setSearchTerm('');

    // Si no hay pacientes (p.ej. por cancelación inicial), vuelve a pedir
    if (patients.length === 0) {
      openFetchCtrlRef.current?.abort();
      const ctrl = new AbortController();
      openFetchCtrlRef.current = ctrl;
      fetchPatients(ctrl.signal);
    }
  };

  // Limpieza del AbortController al desmontar
  useEffect(() => {
    return () => openFetchCtrlRef.current?.abort();
  }, []);

  const handleExistingPatientDialogClose = () => {
    setExistingPatientDialogOpen(false);
    setSearchTerm('');
  };

  const handleNewPatientDialogOpen = () => {
    if (!user) {
      showSnackbar('Por favor inicie sesión para continuar.', 'warning');
      return;
    }
    setNewPatientDialogOpen(true);
  };

  const handleNewPatientDialogClose = () => {
    setNewPatientDialogOpen(false);
    setNewPatientEmail('');
    setNewPatientName('');
  };

  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const filteredPatients = patients.filter((p) =>
    (p.nombres || '').toLowerCase().includes(searchTerm.toLowerCase())
    || (p.apellidos || '').toLowerCase().includes(searchTerm.toLowerCase())
    || (p.email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handlePatientSelect = (patient) => setSelectedPatient(patient);

  const handleInviteNewPatient = async () => {
    if (!newPatientEmail || !newPatientName) {
      showSnackbar('Por favor complete todos los campos', 'warning');
      return;
    }
    if (!user) {
      showSnackbar('No hay sesión activa. Por favor inicie sesión.', 'warning');
      return;
    }
    try {
      const response = await axiosInstance.post('/telemedicine/invite-patient/', {
        email: newPatientEmail,
        name: newPatientName,
      });
      showSnackbar('Invitación enviada exitosamente', 'success');
      handleNewPatientDialogClose();

      if (response.data?.room_id) {
        setRoomId(response.data.room_id);
        setSelectedPatient({ nombres: newPatientName, apellidos: '' });
        setInCall(true);
      }
    } catch (error) {
      console.error('Error sending invitation:', error);
      handleApiError(error);
    }
  };

  const handleStartConsultation = async () => {
    if (!selectedPatient) {
      showSnackbar('Por favor seleccione un paciente', 'warning');
      return;
    }
    if (!user) {
      showSnackbar('No hay sesión activa. Por favor inicie sesión.', 'warning');
      return;
    }

    try {
      const payload = { patient_id: selectedPatient.id_paciente };
      const response = await axiosInstance.post('/telemedicine/start-consultation/', payload);

      setRoomId(response.data.room_id);
      setExistingPatientDialogOpen(false);
      setInCall(true);

      if (response.data.email_sent) {
        showSnackbar(`Consulta iniciada. Se envió un correo a ${selectedPatient.email}.`, 'success');
      } else {
        showSnackbar('Consulta iniciada. El paciente no recibirá correo por falta de email.', 'info');
      }
    } catch (error) {
      console.error('Error starting consultation:', error);
      handleApiError(error);
    }
  };

  const handleEndCall = (isPatient) => {
    setInCall(false);
    setRoomId('');
    setSelectedPatient(null);
    if (isPatient === true) {
      navigate('/thank-you');
    }
  };

  const closeSession = async (sessionId) => {
    try {
      const confirmClose = window.confirm('¿Está seguro que desea cerrar esta consulta? Esta acción no se puede deshacer.');
      if (!confirmClose) return;

      await axiosInstance.post(`/telemedicine/close-session/${sessionId}/`);
      fetchActiveSessions();
      showSnackbar('Consulta cerrada exitosamente', 'success');
    } catch (error) {
      console.error('Error al cerrar la sesión:', error);
      handleApiError(error);
    }
  };

  if (inCall) {
    return (
      <Box sx={{ width: '100%', height: '100%', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden', zIndex: 1300 }}>
        <VideoCallComponent
          roomId={roomId}
          patientName={selectedPatient ? `${selectedPatient.nombres} ${selectedPatient.apellidos}` : 'Paciente'}
          doctorName={user ? `${user.first_name} ${user.last_name}` : 'Doctor'}
          onEndCall={handleEndCall}
          isPatient={false}
        />
      </Box>
    );
  }

  const MotionPaper = motion(Paper);


  return (
    <StyledContainer maxWidth="lg">
      <MotionPaper
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        elevation={0}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 4,
          mb: 3,
          px: { xs: 3, md: 5 },
          py: { xs: 3, md: 4 },
          background: (t) =>
            `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`,
          color: 'white',
        }}
      >
        {/* Burbujas decorativas */}
        <Box
          sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: (t) => alpha('#fff', 0.1),
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: (t) => alpha('#fff', 0.08),
          }}
        />

        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
          }}
        >
          {/* Izquierda: icono + textos */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                bgcolor: (t) => alpha('#fff', 0.2),
                color: 'white',
                borderRadius: '50%',
                width: 60,
                height: 60,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: (t) => `0 8px 32px ${alpha('#000', 0.2)}`,
                flexShrink: 0,
              }}
            >
              <ConnectWithoutContactIcon sx={{ fontSize: 34 }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 800, textShadow: '0 2px 10px rgba(0,0,0,0.25)' }}
              >
                Telemedicina
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Realice consultas médicas virtuales con sus pacientes de forma segura y eficiente.
              </Typography>
            </Box>
          </Box>

          {/* Derecha: espacio reservado para acciones futuras */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
            {/* Espacio para botones de acción si los agregas */}
          </Box>
        </Box>
      </MotionPaper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <StyledCard>
            <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    width: 48,
                    height: 48,
                    mr: 2
                  }}
                >
                  <VideoCallIcon sx={{ fontSize: 28 }} />
                </Avatar>
                <Box>
                  <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                    Consulta con Paciente Existente
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Inicie una videoconsulta con un paciente registrado en su sistema.
                  </Typography>
                </Box>
              </Box>
              <ActionButton
                variant="contained"
                color="primary"
                startIcon={<VideoCallIcon />}
                onClick={handleExistingPatientDialogOpen}
                fullWidth
                size="large"
              >
                Iniciar Consulta
              </ActionButton>
            </CardContent>
          </StyledCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <StyledCard>
            <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar
                  sx={{
                    bgcolor: theme.palette.secondary.main,
                    width: 48,
                    height: 48,
                    mr: 2
                  }}
                >
                  <PersonAddIcon sx={{ fontSize: 28 }} />
                </Avatar>
                <Box>
                  <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                    Invitar Nuevo Paciente
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Envíe una invitación por correo electrónico a un paciente no registrado.
                  </Typography>
                </Box>
              </Box>
              <ActionButton
                variant="contained"
                color="secondary"
                startIcon={<PersonAddIcon />}
                onClick={handleNewPatientDialogOpen}
                fullWidth
                size="large"
              >
                Invitar por Correo
              </ActionButton>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* Existing Patient Dialog */}
      <Dialog
        open={existingPatientDialogOpen}
        onClose={handleExistingPatientDialogClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isSmDown}
        PaperProps={{
          sx: {
            borderRadius: isSmDown ? 0 : 3,
            overflow: 'hidden',
            backgroundColor: theme.palette.mode === 'dark'
              ? theme.palette.background.paper
              : undefined,
            backgroundImage: theme.palette.mode === 'dark'
              ? 'none'
              : undefined
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Seleccionar Paciente
          {isSmDown && (
            <IconButton onClick={handleExistingPatientDialogClose} edge="end">
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 1 }}>
            <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <SearchField
              label="Buscar paciente"
              variant="outlined"
              fullWidth
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Nombre o correo electrónico"
              size="small"
            />
          </Box>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredPatients.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography color="text.secondary">
                No se encontraron pacientes
              </Typography>
            </Box>
          ) : (
            <List sx={{ maxHeight: isSmDown ? 'calc(100dvh - 260px)' : 400, overflow: 'auto' }}>
              {filteredPatients.map(patient => {
                const isSelected = selectedPatient && selectedPatient.id_paciente === patient.id_paciente;
                return (
                  <ListItem
                    key={patient.id_paciente}
                    onClick={() => handlePatientSelect(patient)}
                    selected={isSelected}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: 2,
                      mb: 0.5,
                      backgroundColor: isSelected ?
                        theme.palette.primary.light + '20' : 'transparent',
                      '&:hover': {
                        backgroundColor: isSelected ?
                          theme.palette.primary.light + '30' :
                          theme.palette.action.hover,
                      }
                    }}
                    secondaryAction={patient.email ? (
                      <Tooltip title="Copiar email">
                        <IconButton edge="end" onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(patient.email);
                          showSnackbar('Email copiado', 'success');
                        }}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  >
                    <ListItemIcon>
                      <VideoCallIcon color={isSelected ? 'primary' : 'action'} />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${patient.nombres} ${patient.apellidos}`}
                      secondary={patient.email || 'Sin correo'}
                      primaryTypographyProps={{
                        fontWeight: isSelected ? 600 : 400
                      }}
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button
            onClick={handleExistingPatientDialogClose}
            variant="outlined"
            sx={{ borderRadius: 20, width: { xs: '100%', sm: 'auto' } }}
            fullWidth={isSmDown}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleStartConsultation}
            disabled={!selectedPatient}
            sx={{ borderRadius: 20, width: { xs: '100%', sm: 'auto' } }}
            fullWidth={isSmDown}
          >
            Iniciar Consulta
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Patient Dialog */}
      <Dialog
        open={newPatientDialogOpen}
        onClose={handleNewPatientDialogClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isSmDown}
        PaperProps={{
          sx: {
            borderRadius: isSmDown ? 0 : 3,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Invitar Nuevo Paciente
          {isSmDown && (
            <IconButton onClick={handleNewPatientDialogClose} edge="end">
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Nombre del paciente"
            variant="outlined"
            fullWidth
            value={newPatientName}
            onChange={(e) => setNewPatientName(e.target.value)}
            margin="normal"
            required
            size="small"
            sx={{ mt: 1.5, mb: 2 }}
          />
          <TextField
            label="Correo electrónico del paciente"
            variant="outlined"
            fullWidth
            value={newPatientEmail}
            onChange={(e) => setNewPatientEmail(e.target.value)}
            type="email"
            margin="normal"
            required
            size="small"
          />
          <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
            Se enviará una invitación por correo electrónico con un enlace único para unirse a la consulta de telemedicina.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 3, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button
            onClick={handleNewPatientDialogClose}
            variant="outlined"
            sx={{ borderRadius: 20, width: { xs: '100%', sm: 'auto' } }}
            fullWidth={isSmDown}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<EmailIcon />}
            onClick={handleInviteNewPatient}
            disabled={!newPatientEmail || !newPatientName}
            sx={{ borderRadius: 20, width: { xs: '100%', sm: 'auto' } }}
            fullWidth={isSmDown}
          >
            Enviar Invitación
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sesiones Activas */}
      <Box sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
            Consultas Activas
          </Typography>
          <Button
            onClick={() => fetchActiveSessions()}
            startIcon={<RefreshIcon />}
            size="small"
            variant="outlined"
            sx={{ borderRadius: 20 }}
          >
            Actualizar
          </Button>
        </Box>

        {loadingSessions && (
          <LinearProgress
            sx={{
              mb: 2,
              height: 6,
              borderRadius: 2,
              backgroundColor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.primary.main, 0.15)
                : alpha(theme.palette.primary.main, 0.1),
              '& .MuiLinearProgress-bar': {
                backgroundColor: theme.palette.primary.main
              }
            }}
          />
        )}

        {activeSessions.length === 0 && !loadingSessions ? (
          <StyledCard>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                No hay consultas activas en este momento.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Cuando inicie una consulta de telemedicina, aparecerá aquí para que pueda reincorporarse fácilmente desde su móvil.
              </Typography>
            </CardContent>
          </StyledCard>
        ) : (
          <Grid container spacing={2}>
            {activeSessions.map((session) => {
              const start = session.start_time ? new Date(session.start_time) : null;
              const startLabel = start ? start.toLocaleString() : 'Fecha no disponible';
              return (
                <Grid item xs={12} sm={6} md={4} key={session.id}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: { xs: 1.5, sm: 2 },
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.25,
                      borderRadius: 2,
                      height: '100%',
                      backgroundColor: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.primary.main, 0.12)
                        : alpha(theme.palette.primary.main, 0.05),
                      border: theme.palette.mode === 'dark'
                        ? `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                        : 'none',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.shadows[4]
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar
                        sx={{
                          bgcolor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                          width: 36, height: 36
                        }}
                      >
                        <VideocamIcon fontSize="small" />
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap title={session.patient_name || 'Paciente Invitado'}>
                          {session.patient_name || "Paciente Invitado"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Iniciada: {startLabel}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 1,
                      mt: 0.5
                    }}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => rejoinSession(session.room_id, session.id, session.patient_name)}
                        startIcon={<VideocamIcon />}
                        sx={{ borderRadius: 20, flex: 1 }}
                      >
                        Reincorporarse
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => closeSession(session.id)}
                        startIcon={<CloseIcon />}
                        sx={{ borderRadius: 20, flex: 1 }}
                      >
                        Terminar
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            borderRadius: 3,
            boxShadow: 3
          }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Divisor visual con Chip */}
      <Box
        sx={{
          width: '100%',
          mt: 6,
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}
      >
        <Divider sx={{ flexGrow: 1 }} />
        <Chip
          label="Preguntas frecuentes"
          color="primary"
          variant="outlined"
          size="medium"
          sx={{
            fontWeight: 'medium',
            px: 1.5,
            borderWidth: 1.5,
            backgroundColor: theme.palette.mode === 'dark'
              ? alpha(theme.palette.primary.main, 0.15)
              : alpha(theme.palette.primary.main, 0.12),
            borderColor: theme.palette.mode === 'dark'
              ? alpha(theme.palette.primary.main, 0.3)
              : alpha(theme.palette.primary.main, 0.5),
          }}
        />
        <Divider sx={{ flexGrow: 1 }} />
      </Box>

      {/* Preguntas Frecuentes */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{
            height: '100%',
            borderRadius: 3,
            transition: 'all 0.3s ease',
            backgroundColor: theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.paper, 0.4)
              : undefined,
            border: theme.palette.mode === 'dark'
              ? `1px solid ${alpha(theme.palette.divider, 0.1)}`
              : 'none',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: theme.shadows[8],
              backgroundColor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.primary.main, 0.15)
                : alpha(theme.palette.primary.main, 0.04),
              cursor: 'pointer'
            }
          }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
                ¿Qué hago si el paciente no recibe el correo?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Verifique que el correo electrónico del paciente esté correctamente registrado en el sistema.
                También puede compartir el enlace manualmente copiándolo desde la notificación que aparece
                al iniciar la consulta.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{
            height: '100%',
            borderRadius: 3,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: theme.shadows[8],
              backgroundColor: alpha(theme.palette.primary.main, 0.04),
              cursor: 'pointer'
            }
          }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
                ¿Funciona en dispositivos móviles?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                La respuesta es, Sí. La videollamada se puede realizar desde cualquier dispositivo, sea Windows, Safari, Android o iOS.
                Funciona con la mayoría de navegadores web, asegurándose de tener los permisos activos.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{
            height: '100%',
            borderRadius: 3,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: theme.shadows[8],
              backgroundColor: alpha(theme.palette.primary.main, 0.04),
              cursor: 'pointer'
            }
          }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
                ¿Cómo soluciono problemas de audio o video?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Asegúrese de que su navegador tenga permisos para acceder a la cámara y micrófono.
                Si hay problemas durante la llamada, tanto usted como el paciente pueden intentar
                actualizar la página o reiniciar el navegador.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Nueva Pregunta Frecuente */}
        <Grid item xs={12} md={6}>
          <Card sx={{
            height: '100%',
            borderRadius: 3,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: theme.shadows[8],
              backgroundColor: alpha(theme.palette.primary.main, 0.04),
              cursor: 'pointer'
            }
          }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
                ¿Por qué veo una consulta activa cuando no he hecho ninguna?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Es probable que sea una consulta creada automáticamente cuando un paciente agendó una cita a través del portal.
                El sistema genera la sala de telemedicina y envía el enlace por correo al paciente. Cuando se acerque la hora de la cita,
                podrás reincorporarte desde “Consultas Activas” para atenderlo.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </StyledContainer>
  );
};

export default TelemedicinePage;
